// components/DevicePreview/device-preview.tsx
import { MoveRight } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'ui';
import { getSelectedSiteInfo } from '../../../store/siteInfoSlice';
import { getSelectedTaskId, setSelectedTaskId } from '../../../store/tasksSlice';
import { getDisplayMode } from '../../../store/uiSlice';

// Define device dimensions with fixed pixel values
const deviceDimensions = {
    desktop: {
        width: '100%',
        height: '100%',
    },
    tablet: {
        width: '768px',
        height: '1024px',
    },
    mobile: {
        width: '390px',
        height: '844px',
    },
};

// Helper to check if we're in a Chrome extension context
const isChromeExtension = () => {
    return typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.id;
};

// Bottom padding for the device preview
const BOTTOM_PADDING = '75px';

export const DevicePreview = () => {
    const dispatch = useDispatch();
    const displayMode = useSelector(getDisplayMode);
    const containerRef = useRef<HTMLDivElement>(null);
    const deviceContainerRef = useRef<HTMLDivElement>(null);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const selectedTaskId = useSelector(getSelectedTaskId);
    const siteInfo = useSelector(getSelectedSiteInfo);
    const isUpdatingFromIframeRef = useRef(false);
    const [iframeUrl, setIframeUrl] = useState<string>('');
    const [deviceWarning, setDeviceWarning] = useState(true);
    const [viewportDimensions, setViewportDimensions] = useState({
        width: typeof window !== 'undefined' ? window.innerWidth : 0,
        height: typeof window !== 'undefined' ? window.innerHeight : 0,
    });

    // Handle viewport resizing
    useEffect(() => {
        const handleResize = () => {
            setViewportDimensions({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        // Initial call to set dimensions
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Prevent scrolling in the background when device preview is active
    useEffect(() => {
        // Save the original overflow style
        const originalOverflow = document.body.style.overflow;
        const originalHtmlOverflow = document.documentElement.style.overflow;

        // Prevent scrolling in the main document
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';

        // Handler for wheel events to prevent scrolling
        const preventScroll = (e: WheelEvent) => {
            // Check if the event target is not within the device preview
            if (!containerRef.current?.contains(e.target as Node)) {
                e.preventDefault();
                return false;
            }
            return true;
        };

        // Add wheel event listener with passive: false to allow preventDefault
        window.addEventListener('wheel', preventScroll, { passive: false });

        // Cleanup function to restore original styles when component unmounts
        return () => {
            document.body.style.overflow = originalOverflow;
            document.documentElement.style.overflow = originalHtmlOverflow;
            // Remove the event listener
            window.removeEventListener('wheel', preventScroll);
        };
    }, []);

    useEffect(() => {
        const baseUrl = window.location.origin + window.location.pathname;
        const searchP = new URLSearchParams(window.location.search);
        searchP.append("embedded", "true");
        if (siteInfo) {
            searchP.append("siteId", siteInfo.id);
        }
        // Add special parameter to trigger injection
        if (isChromeExtension()) {
            searchP.append("atarimcollab", "true");
        }
        setIframeUrl(`${baseUrl}?${searchP.toString()}`);
    }, [siteInfo]);

    const handleIframeLoad = () => {
        if (!iframeRef.current || !iframeRef.current.contentWindow) return;

        if (isChromeExtension()) {
            try {
                // Notify background script to inject into this iframe
                chrome.runtime.sendMessage({
                    type: 'INJECT_INTO_IFRAME',
                    frameUrl: iframeRef.current.src,
                    iframeSrc: iframeRef.current.src // Provide the iframe src URL
                }, (response) => {
                    // After successful injection, establish communication
                    if (response?.success) {
                        setTimeout(() => {
                            try {
                                iframeRef.current?.contentWindow?.postMessage({
                                    type: 'PARENT_CONNECTED',
                                    taskId: selectedTaskId
                                }, '*');
                            } catch (error) {
                                console.error('Failed to send initial message to iframe:', error);
                            }
                        }, 500);
                    }
                });
            } catch (error) {
                console.error('Failed to communicate with extension background:', error);
            }
        }
    };

    useEffect(() => {
        const handleMessageFromIframe = (event: MessageEvent) => {
            // Make sure the message is from our iframe
            if (event.source === iframeRef.current?.contentWindow) {
                if (event.data.type === 'IFRAME_TASK_ID_CHANGED') {
                    const newTaskId = event.data.taskId;

                    // Update the parent's task ID state without triggering another update to iframe
                    if (!isUpdatingFromIframeRef.current) {
                        isUpdatingFromIframeRef.current = true;
                        dispatch(setSelectedTaskId(newTaskId));

                        // Reset the flag after a short delay
                        setTimeout(() => {
                            isUpdatingFromIframeRef.current = false;
                        }, 50);
                    }
                }
            }
        };

        window.addEventListener('message', handleMessageFromIframe);
        return () => window.removeEventListener('message', handleMessageFromIframe);
    }, [dispatch]);

    useEffect(() => {
        if (selectedTaskId && iframeRef.current && iframeRef.current.contentWindow && !isUpdatingFromIframeRef.current) {
            try {
                iframeRef.current.contentWindow.postMessage({
                    type: 'UPDATE_TASK_ID',
                    taskId: selectedTaskId
                }, '*');
            } catch (error) {
                console.error('Failed to send message to iframe:', error);
            }
        }
    }, [selectedTaskId]);

    return (
        <div
            id='device-preview'
            ref={containerRef}
            className={`fixed inset-0 w-full h-screen overflow-hidden flex items-center pointer-events-auto justify-center`}
            style={{
                scrollBehavior: 'smooth',
                overscrollBehavior: 'contain',
                background: `url('https://atarim-wpfeedback-image.s3.eu-west-1.amazonaws.com/images/responsive_mode_bg_1.jpg')`,
                backgroundColor: '#f5f5f5', // This serves as fallback if image fails to load
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                paddingBottom: BOTTOM_PADDING,
            }}
        >
            {/* Device warning modal */}
            <AnimatePresence mode="sync">
                {displayMode !== 'desktop' && viewportDimensions.height < (parseInt(deviceDimensions[displayMode].height) + 75) && deviceWarning && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        key={'device-warning-modal'}
                        className="fixed top-0 left-0 w-full h-full z-50 bg-background/90 backdrop-blur-lg flex items-center justify-center ">
                        <motion.div
                            initial={{ opacity: 0, y: 60 }}
                            animate={{
                                opacity: 1, y: 0,
                                transition: { duration: 0.3, delay: 0.2 }

                            }}
                            exit={{ opacity: 0, y: 40 }}

                            key={'device-warning-content'}
                            className='flex flex-col items-center justify-center'>
                            <img src="https://atarim-wpfeedback-image.s3.eu-west-1.amazonaws.com/images/scale-exceeded.svg" alt="" className='max-w-[175px] mb-2' />
                            <h3 className='text-lg text-textcolor font-bold'>Device Preview Out of Bounds</h3>
                            <span className='flex gap-1 flex-col items-center mt-2 mb-6'>
                                <p className="text-textcolor text-sm">Your current browser window is smaller than the selected device preview size.</p>
                                <p className="text-textcolor text-sm">We recommend resizing your browser or using a larger screen for an accurate preview.<span className='font-semibold'> Want to proceed anyway?</span></p>
                            </span>
                            <Button onClick={() => setDeviceWarning(false)} rightIcon={<MoveRight size={18} />} size="sm">Proceed Anyway</Button>
                        </motion.div>
                    </motion.div>
                )
                }
            </AnimatePresence>
            {/* Device selection buttons */}
            <div
                className="flex items-center justify-center h-full"
                style={{
                    aspectRatio: `${parseInt(deviceDimensions[displayMode].width)} / ${parseInt(deviceDimensions[displayMode].height)}`,
                    maxHeight: `${parseInt(deviceDimensions[displayMode].height)}px`,
                }}
            >
                <div
                    ref={deviceContainerRef}
                    className={`relative transition-all duration-300 ${displayMode !== 'desktop' ? 'rounded-2xl bg-background h-full' : ''}`}
                    style={{
                        aspectRatio: `${parseInt(deviceDimensions[displayMode].width)} / ${parseInt(deviceDimensions[displayMode].height)}`,
                        ...(displayMode !== 'desktop' ? {
                            boxShadow: '0 0 0 1px color-mix(in oklab, #0000330f, #f0f0f3 25%), 0 12px 60px #00000026, 0 16px 64px #00005506, 0 16px 36px 20px #00062e12',
                            transformOrigin: 'center',
                        } : {})
                    }}
                >
                    <iframe
                        ref={iframeRef}
                        src={iframeUrl}
                        className="w-full h-full bg-background rounded-xl"
                        sandbox="allow-same-origin allow-scripts allow-forms"
                        allow="fullscreen"
                        loading="lazy"
                        onLoad={handleIframeLoad}
                    />
                </div>
            </div>

        </div>
    );
};